import { Box, IconButton, Paper, Stack, Button, Divider, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Typography from "@tiptap/extension-typography";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { useEffect } from "react";
import "../RichEditor.css";

interface Props {
    block: TextBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const TextBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Highlight,
            Typography,
            Image,
            Link.configure({
                openOnClick: false,
            }),
            TextAlign.configure({
                types: ["heading", "paragraph"],
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: block.content.html,
        onUpdate: ({ editor }) => {
            onChange({ html: editor.getHTML() });
        },
    });

    useEffect(() => {
        if (editor && block.content.html !== editor.getHTML()) {
            editor.commands.setContent(block.content.html);
        }
    }, [editor, block.content.html]);

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    {editor && (
                        <Box sx={{ mb: 1 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Tooltip title="Insert Table">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<TableChartIcon />}
                                        onClick={() =>
                                            editor
                                                .chain()
                                                .focus()
                                                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                                                .run()
                                        }
                                    >
                                        Table
                                    </Button>
                                </Tooltip>
                                {editor.isActive("table") && (
                                    <>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => editor.chain().focus().addColumnBefore().run()}
                                        >
                                            Add Column Before
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                                        >
                                            Add Column After
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => editor.chain().focus().deleteColumn().run()}
                                        >
                                            Delete Column
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => editor.chain().focus().addRowBefore().run()}
                                        >
                                            Add Row Before
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => editor.chain().focus().addRowAfter().run()}
                                        >
                                            Add Row After
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => editor.chain().focus().deleteRow().run()}
                                        >
                                            Delete Row
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            color="error"
                                            onClick={() => editor.chain().focus().deleteTable().run()}
                                        >
                                            Delete Table
                                        </Button>
                                    </>
                                )}
                            </Stack>
                            <Divider sx={{ my: 1 }} />
                        </Box>
                    )}
                    <EditorContent editor={editor} />
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default TextBlockEditor;
